import * as THREE from 'three';
import { Object3D } from 'three';

import { ConstantsOpenPose } from '../constants_open_pose';
import { EOpenPoseBodyBone } from '../enums/e_open_pose_body_bone';
import { EOpenPoseBodyDataMode } from '../enums/e_open_pose_body_data_mode';
import { EOpenPoseBodyGroup } from '../enums/e_open_pose_body_group';
import { EOpenPoseBodyNode } from '../enums/e_open_pose_body_node';
import { OpenPoseBodyData } from '../open_pose_types/open_pose_body_data';
import { OpenPoseBodyBone } from './open_pose_body_bone';
import { OpenPoseBodyNode } from './open_pose_body_node';
import { create_or_update_bone_link_cylinder, is_need_save_object } from './open_pose_body_utils';

export class OpenPoseBodyController {
  /*
   * FIELDS
   */
  body: Object3D;

  nodes!: Record<string, OpenPoseBodyNode>;
  groups!: Record<string, Object3D>;
  bones!: Record<string, OpenPoseBodyBone>;

  scale: number = 1;

  /*
   * METHODS
   */
  constructor(object_3d: Object3D) {
    this.body = object_3d;

    this.init_nodes();
    this.init_groups();
    this.init_bones();
  }

  private init_nodes(): void {
    this.nodes = {};
    this.body.traverse(object => {
      if (ConstantsOpenPose.OPEN_POSE_BODY_NODES_LIST.includes(object.name)) {
        this.nodes[object.name] = new OpenPoseBodyNode(object.name, object);
      }
    });
  }

  private init_groups(): void {
    this.groups = {};
    this.body.traverse(object => {
      if (ConstantsOpenPose.OPEN_POSE_BODY_GROUPS_LIST.includes(object.name)) {
        this.groups[object.name] = object;
      }
    });
    this.groups[EOpenPoseBodyGroup.TORSO] = this.body;
  }

  private init_bones(): void {
    this.bones = {};

    for (const [start_node_name, end_node_name, bone_name] of ConstantsOpenPose.BONE_CONNECTIONS) {
      const node_start = this.nodes[start_node_name];
      const node_end = this.nodes[end_node_name] || this.groups[end_node_name];

      if (!node_start) continue;
      if (!node_end) continue;

      const object_start = node_start.object;
      const object_end = node_end instanceof OpenPoseBodyNode ? node_end.object : node_end;

      const world_position_start = new THREE.Vector3();
      const world_position_end = new THREE.Vector3();

      object_start.getWorldPosition(world_position_start);
      object_end.getWorldPosition(world_position_end);

      const initial_distance = world_position_start.distanceTo(world_position_end);

      this.bones[bone_name] = new OpenPoseBodyBone(bone_name, object_start, object_end, initial_distance);
    }
  }

  create_or_update_bones(is_create: boolean = false, use_stored_distance: boolean = false): void {
    const final_thickness = ConstantsOpenPose.BONE_THICKNESS * this.scale;

    // Force update all matrices to ensure accurate positions
    this.body.updateMatrixWorld(true);
    
    // Also update all node matrices individually
    for (const node_name in this.nodes) {
      this.nodes[node_name].object.updateMatrixWorld(true);
    }

    for (const node_name in this.nodes) this.update_bone_link_for_node(node_name, final_thickness, is_create, use_stored_distance);

    // if (!is_create) this.body.updateMatrixWorld(true);
  }


  private update_bone_link_for_node(name: string, thickness = ConstantsOpenPose.BONE_THICKNESS, is_create = false, use_stored_distance = false): void {
    const node = this.nodes[name];

    const special_nodes = [EOpenPoseBodyNode.HIP_LEFT, EOpenPoseBodyNode.HIP_RIGHT, EOpenPoseBodyNode.SHOULDER_RIGHT, EOpenPoseBodyNode.SHOULDER_LEFT];

    if (special_nodes.includes(name as any)) {
      const inner_name = `${name}_inner`;
      const inner_group = this.groups[inner_name];

      if (inner_group) {
        // link from the inner group (anchored near the torso/neck cluster) to the node object
        create_or_update_bone_link_cylinder({
          start_object: inner_group,
          end_object: node.object,
          start_label_name: EOpenPoseBodyNode.NECK,
          end_label_name: name,
          thickness,
          is_create,
          body_controller: this,
          use_stored_distance,
        });
      }
      return;
    }

    if (name !== EOpenPoseBodyNode.NECK) {
      const parent = node.object.parent;
      if (parent) {
        // generic case: link from parent to the node
        create_or_update_bone_link_cylinder({
          start_object: parent,
          end_object: node.object,
          start_label_name: parent.name,
          end_label_name: node.object.name,
          thickness,
          is_create,
          body_controller: this,
          use_stored_distance,
        });
      }
    }
  }


  get_body_data(): OpenPoseBodyData {
    const result: OpenPoseBodyData = {
      position: this.body.position.toArray(),
      rotation: this.body.rotation.toArray(),
      scale: this.body.scale.toArray(),
      child: {},
    };

    this.body.traverse(child => {
      if (child.name && is_need_save_object(child.name)) {
        const data: Pick<OpenPoseBodyData, EOpenPoseBodyDataMode> = {};

        if (this.get_distance_of(child.position, new THREE.Vector3(0, 0, 0)) !== 0) {
          data.position = child.position.toArray();
        }
        if (this.get_distance_of(child.scale, new THREE.Vector3(1, 1, 1)) !== 0) {
          data.scale = child.scale.toArray();
        }
        if (child.rotation.x !== 0 || child.rotation.y !== 0 || child.rotation.z !== 0) {
          data.rotation = child.rotation.toArray();
        }

        if (Object.keys(data).length > 0) result.child[child.name] = data;
      }
    });

    return result;
  }

  get_world_position(object_3d: Object3D): THREE.Vector3 {
    const position = new THREE.Vector3();
    if (!object_3d) debugger;
    object_3d.getWorldPosition(position);

    return position;
  }

  get_distance_of(from: THREE.Vector3, to: THREE.Vector3): number {
    return from.distanceTo(to);
  }

  set_current_scale(scale: number): void {
    this.scale = scale;
  }

  get_bone(bone_name: string): OpenPoseBodyBone {
    return this.bones[bone_name];
  }

  get_node(node_name: string): OpenPoseBodyNode {
    return this.nodes[node_name];
  }

  get_group(group_name: string): Object3D {
    return this.groups[group_name];
  }

  get_bones_for_node(node_name: string): OpenPoseBodyBone[] {
    const connected_bones: OpenPoseBodyBone[] = [];
    for (const bone_name in this.bones) {
      const bone = this.bones[bone_name];
      if (bone.object_start.name === node_name || bone.object_end.name === node_name) {
        connected_bones.push(bone);
      }
    }
    return connected_bones;
  }

  set_bone_distance(bone_name: string, distance: number): boolean {
    const bone = this.bones[bone_name];
    if (!bone) return false;
    bone.set_distance(distance);
    return true;
  }

  set_bone_stored_distance(bone_name: string, distance: number): boolean {
    const bone = this.bones[bone_name];
    if (!bone) return false;
    bone.set_stored_distance(distance);
    return true;
  }

  get_bone_distance(bone_name: string): number {
    const bone = this.bones[bone_name];

    return bone.get_distance();
  }

  get_bones() {
    return this.bones;
  }

  get_bone_length_for_node(node_name: string): number {
    console.log(`[BONE_LENGTH] Getting bone length for node: ${node_name}`);
    console.log(`[BONE_LENGTH] Available bones:`, Object.keys(this.bones));
    
    for (const bone_name in this.bones) {
      const bone = this.bones[bone_name];
      console.log(`[BONE_LENGTH] Checking bone ${bone_name}: end=${bone.object_end.name}`);
      if (bone.object_end.name !== node_name) continue;

      const distance = bone.get_distance();
      console.log(`[BONE_LENGTH] Found matching bone ${bone_name} with distance: ${distance}`);
      return distance;
    }

    console.log(`[BONE_LENGTH] No direct bone found, trying switch case for ${node_name}`);
    
    switch (node_name) {
      // Arms
      case EOpenPoseBodyNode.ELBOW_LEFT:
        console.log(`[BONE_LENGTH] Trying UPPER_ARM_LEFT for ELBOW_LEFT`);
        return this.get_bone_distance(EOpenPoseBodyBone.UPPER_ARM_LEFT);
      case EOpenPoseBodyNode.ELBOW_RIGHT:
        console.log(`[BONE_LENGTH] Trying UPPER_ARM_RIGHT for ELBOW_RIGHT`);
        return this.get_bone_distance(EOpenPoseBodyBone.UPPER_ARM_RIGHT);
      case EOpenPoseBodyNode.WRIST_LEFT:
        console.log(`[BONE_LENGTH] Trying FOREARM_LEFT for WRIST_LEFT`);
        return this.get_bone_distance(EOpenPoseBodyBone.FOREARM_LEFT);
      case EOpenPoseBodyNode.WRIST_RIGHT:
        console.log(`[BONE_LENGTH] Trying FOREARM_RIGHT for WRIST_RIGHT`);
        return this.get_bone_distance(EOpenPoseBodyBone.FOREARM_RIGHT);

      // Legs
      case EOpenPoseBodyNode.HIP_LEFT:
        console.log(`[BONE_LENGTH] Trying THIGH_LEFT for HIP_LEFT`);
        return this.get_bone_distance(EOpenPoseBodyBone.THIGH_LEFT);
      case EOpenPoseBodyNode.HIP_RIGHT:
        console.log(`[BONE_LENGTH] Trying THIGH_RIGHT for HIP_RIGHT`);
        return this.get_bone_distance(EOpenPoseBodyBone.THIGH_RIGHT);
      case EOpenPoseBodyNode.KNEE_LEFT:
        console.log(`[BONE_LENGTH] Trying UPPER_LEG_LEFT for KNEE_LEFT`);
        return this.get_bone_distance(EOpenPoseBodyBone.UPPER_LEG_LEFT);
      case EOpenPoseBodyNode.KNEE_RIGHT:
        console.log(`[BONE_LENGTH] Trying UPPER_LEG_RIGHT for KNEE_RIGHT`);
        return this.get_bone_distance(EOpenPoseBodyBone.UPPER_LEG_RIGHT);
      case EOpenPoseBodyNode.ANKLE_LEFT:
        console.log(`[BONE_LENGTH] Trying LOWER_LEG_LEFT for ANKLE_LEFT`);
        return this.get_bone_distance(EOpenPoseBodyBone.LOWER_LEG_LEFT);
      case EOpenPoseBodyNode.ANKLE_RIGHT:
        console.log(`[BONE_LENGTH] Trying LOWER_LEG_RIGHT for ANKLE_RIGHT`);
        return this.get_bone_distance(EOpenPoseBodyBone.LOWER_LEG_RIGHT);

      // Shoulders
      case EOpenPoseBodyNode.SHOULDER_LEFT:
        console.log(`[BONE_LENGTH] Trying SHOULDER_LEFT for SHOULDER_LEFT`);
        return this.get_bone_distance(EOpenPoseBodyBone.SHOULDER_LEFT);
      case EOpenPoseBodyNode.SHOULDER_RIGHT:
        console.log(`[BONE_LENGTH] Trying SHOULDER_RIGHT for SHOULDER_RIGHT`);
        return this.get_bone_distance(EOpenPoseBodyBone.SHOULDER_RIGHT);

      // Head
      case EOpenPoseBodyNode.NOSE:
        console.log(`[BONE_LENGTH] Trying NECK_TO_NOSE for NOSE`);
        return this.get_bone_distance(EOpenPoseBodyBone.NECK_TO_NOSE);

      // Eyes and ears
      case EOpenPoseBodyNode.EYE_LEFT:
        console.log(`[BONE_LENGTH] Trying NOSE_TO_EYE_LEFT for EYE_LEFT`);
        return this.get_bone_distance(EOpenPoseBodyBone.NOSE_TO_EYE_LEFT);
      case EOpenPoseBodyNode.EYE_RIGHT:
        console.log(`[BONE_LENGTH] Trying NOSE_TO_EYE_RIGHT for EYE_RIGHT`);
        return this.get_bone_distance(EOpenPoseBodyBone.NOSE_TO_EYE_RIGHT);
      case EOpenPoseBodyNode.EAR_LEFT:
        console.log(`[BONE_LENGTH] Trying EYE_TO_EAR_LEFT for EAR_LEFT`);
        return this.get_bone_distance(EOpenPoseBodyBone.EYE_TO_EAR_LEFT);
      case EOpenPoseBodyNode.EAR_RIGHT:
        console.log(`[BONE_LENGTH] Trying EYE_TO_EAR_RIGHT for EAR_RIGHT`);
        return this.get_bone_distance(EOpenPoseBodyBone.EYE_TO_EAR_RIGHT);

      default:
        console.log(`[BONE_LENGTH] No bone found for ${node_name}, returning 0`);
        return 0;
    }
  }

  has_node(node_name: string): boolean {
    return node_name in this.nodes;
  }
}
